import React, {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {keyframes} from '@emotion/react';
import {useTranslation} from 'react-i18next';

/*
 * "AI is composing a reply" placeholder shown where the assistant
 * message will appear. Shimmering text that cycles through a few short
 * status phrases — feels alive, doesn't add a spinner or dot to the
 * visual language. Every time the indicator mounts (a new reply starts)
 * the cycle restarts at "Thinking…".
 */

const PHRASES = [
    'Thinking…',
    'Reading the docs…',
    'Checking the dashboard…',
    'Cross-referencing…',
    'Looking up your data…',
    'Composing the answer…',
    'Working through it…',
    'Putting it together…',
    'Drafting your reply…',
    'Almost there…',
];

const ROW_HEIGHT = 18;
const PHRASE_INTERVAL_MS = 2200;
const SLIDE_DURATION_MS = 500;

const shimmer = keyframes`
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
`;

const ShimmerText: React.FC<{children: React.ReactNode}> = ({children}) => (
    <Box
        component="span"
        sx={{
            fontSize: 13,
            fontWeight: 500,
            backgroundImage:
                'linear-gradient(90deg, #6b7280 0%, #9aa0a6 40%, #d1d5db 50%, #9aa0a6 60%, #6b7280 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            animation: `${shimmer} 3.6s linear infinite`,
        }}
    >
        {children}
    </Box>
);

const AssistantTypingIndicator: React.FC = () => {
    const {t} = useTranslation();
    const [i, setI] = useState(0);
    const [animate, setAnimate] = useState(true);

    useEffect(() => {
        const id = window.setInterval(
            () => setI(curr => curr + 1),
            PHRASE_INTERVAL_MS,
        );
        return () => window.clearInterval(id);
    }, []);

    // Slot-machine wrap: we render a duplicate of the first phrase after
    // the list so transitioning from "last" → "first" continues sliding
    // up. Once the duplicate is fully visible, snap back to index 0 with
    // no transition so the next interval picks up cleanly.
    useEffect(() => {
        if (i !== PHRASES.length) return;
        const tid = window.setTimeout(() => {
            setAnimate(false);
            setI(0);
            requestAnimationFrame(() =>
                requestAnimationFrame(() => setAnimate(true)),
            );
        }, SLIDE_DURATION_MS);
        return () => window.clearTimeout(tid);
    }, [i]);

    return (
        <Box
            sx={{
                alignSelf: 'flex-start',
                position: 'relative',
                height: ROW_HEIGHT,
                overflow: 'hidden',
                minWidth: 160,
                py: 0.5,
            }}
            aria-label={t('Crqlar Assistant is generating a response')}
            role="status"
        >
            <Box
                sx={{
                    transform: `translateY(${-i * ROW_HEIGHT}px)`,
                    transition: animate
                        ? `transform ${SLIDE_DURATION_MS}ms ease`
                        : 'none',
                }}
            >
                {PHRASES.map(p => (
                    <Box
                        key={p}
                        sx={{
                            height: ROW_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <ShimmerText>{t(p)}</ShimmerText>
                    </Box>
                ))}
                {/* trailing duplicate of the first phrase for seamless wrap */}
                <Box
                    key="wrap"
                    sx={{
                        height: ROW_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <ShimmerText>{t(PHRASES[0])}</ShimmerText>
                </Box>
            </Box>
        </Box>
    );
};

export default AssistantTypingIndicator;
